import { useEffect, useMemo, useRef, useState } from 'react';
import { AppNode, Edge, ProcessDefinition, Swimlane } from '../types';
import { validateDiagram, summarize } from '../utils/validation';
import type { ValidationIssue } from '../utils/validation';

interface UseValidationArgs {
  processes: ProcessDefinition[];
  activeProcessId: string;
  nodes: AppNode[];
  edges: Edge[];
  swimlanes?: Swimlane[];
  /** Debounce delay for re-running rules on edits. Default 400ms. */
  debounceMs?: number;
}

export interface UseValidationResult {
  issues: ValidationIssue[];
  errors: number;
  warnings: number;
  isValidating: boolean;
  /** Force an immediate re-run (bypasses debounce). */
  revalidate: () => void;
}

/**
 * Live validation of the current diagram. Runs `validateDiagram` after a
 * short debounce on each change to avoid thrashing while the user types.
 *
 * The active process's in-editor state (nodes/edges/swimlanes) is merged
 * into the processes array before validation — the processes snapshot is
 * only updated when the user switches processes, so live edits would
 * otherwise be invisible to rules.
 */
export const useValidation = ({
  processes,
  activeProcessId,
  nodes,
  edges,
  swimlanes,
  debounceMs = 400,
}: UseValidationArgs): UseValidationResult => {
  const [issues, setIssues] = useState<ValidationIssue[]>([]);
  const [isValidating, setIsValidating] = useState(false);
  const timerRef = useRef<number | null>(null);

  const mergedProcesses = useMemo(
    () => processes.map(p => (p.id === activeProcessId ? { ...p, nodes, edges, swimlanes: swimlanes ?? p.swimlanes } : p)),
    [processes, activeProcessId, nodes, edges, swimlanes]
  );

  const run = useMemo(
    () => () => {
      setIsValidating(true);
      try {
        const next = validateDiagram(mergedProcesses);
        setIssues(next);
      } catch (err) {
        console.error('[useValidation] validation crashed:', err);
      } finally {
        setIsValidating(false);
      }
    },
    [mergedProcesses]
  );

  useEffect(() => {
    if (timerRef.current !== null) window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(run, debounceMs);
    return () => {
      if (timerRef.current !== null) window.clearTimeout(timerRef.current);
    };
  }, [run, debounceMs]);

  const { errors, warnings } = useMemo(() => summarize(issues), [issues]);

  return { issues, errors, warnings, isValidating, revalidate: run };
};
